import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Loader2, CheckCircle2, CreditCard, FileText, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { API } from "@/utils/constants";

const fmt = (v, cur = "USD") => new Intl.NumberFormat("en-US", { style: "currency", currency: cur || "USD" }).format(v || 0);
const STATUS_CLS = {
  draft: "bg-slate-500/15 text-slate-600",
  sent: "bg-sky-500/15 text-sky-600",
  paid: "bg-emerald-500/15 text-emerald-600",
  overdue: "bg-red-500/15 text-red-600",
};

const BizHeader = ({ business }) => (
  <div className="flex items-center gap-3 mb-6">
    {business?.logo && <img src={business.logo} alt="logo" className="h-10 max-w-[140px] object-contain" />}
    <div>
      <p className="font-bold text-lg leading-tight">{business?.business_name || "Invoice"}</p>
      {business?.business_website && <p className="text-xs text-muted-foreground">{business.business_website}</p>}
    </div>
  </div>
);

export default function PayInvoicePage({ token }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [paying, setPaying] = useState(false);
  const [payStatus, setPayStatus] = useState(null);
  const sessionId = new URLSearchParams(window.location.search).get("session_id");

  useEffect(() => {
    axios.get(`${API}/public/invoice/${token}`, { withCredentials: false })
      .then((r) => setData(r.data))
      .catch(() => setError("Invoice not found or link expired."));
  }, [token]);

  const poll = useCallback(async (attempt = 0) => {
    if (attempt > 8) { setPayStatus("timeout"); return; }
    try {
      const r = await axios.get(`${API}/public/invoice/${token}/payment-status/${sessionId}`, { withCredentials: false });
      if (r.data.payment_status === "paid") { setPayStatus("paid"); setData((d) => d ? { ...d, invoice: { ...d.invoice, status: "paid" } } : d); return; }
      if (r.data.payment_status === "expired") { setPayStatus("failed"); return; }
      setTimeout(() => poll(attempt + 1), 2000);
    } catch { setPayStatus("failed"); }
  }, [token, sessionId]);

  useEffect(() => { if (sessionId) { setPayStatus("checking"); poll(); } }, [sessionId, poll]);

  const startPayment = async () => {
    setPaying(true);
    try {
      const r = await axios.post(`${API}/public/invoice/${token}/checkout`, { origin_url: window.location.origin }, { withCredentials: false });
      window.location.href = r.data.url;
    } catch (err) {
      setError(err.response?.data?.detail || "Could not start payment.");
      setPaying(false);
    }
  };

  if (error && !data) return <div className="min-h-screen flex items-center justify-center bg-muted/30 p-6"><p className="text-sm text-muted-foreground" data-testid="pay-error">{error}</p></div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  const inv = data.invoice;
  const isPaid = inv.status === "paid" || payStatus === "paid";

  return (
    <div className="min-h-screen bg-muted/30 py-10 px-4" data-testid="pay-invoice-page">
      <div className="max-w-xl mx-auto bg-card border rounded-2xl shadow-sm p-6 sm:p-8">
        <BizHeader business={data.business} />
        {payStatus === "checking" && <div className="flex items-center gap-2 text-sm bg-sky-500/10 border border-sky-500/25 rounded-lg p-3 mb-4" data-testid="pay-checking"><Loader2 className="h-4 w-4 animate-spin" />Confirming your payment…</div>}
        {isPaid && <div className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-500/10 border border-emerald-500/25 rounded-lg p-3 mb-4" data-testid="pay-success"><CheckCircle2 className="h-4 w-4" />This invoice has been paid. Thank you!</div>}
        {(payStatus === "failed" || payStatus === "timeout") && <div className="text-sm text-red-600 bg-red-500/10 border border-red-500/25 rounded-lg p-3 mb-4" data-testid="pay-failed">Payment not completed — you can try again below.</div>}

        <div className="flex items-start justify-between mb-5">
          <div>
            <h1 className="text-xl font-bold" data-testid="pay-invoice-number">Invoice {inv.number}</h1>
            <p className="text-sm text-muted-foreground">Billed to {inv.client_name}{inv.client_company ? ` · ${inv.client_company}` : ""}</p>
          </div>
          <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${STATUS_CLS[isPaid ? "paid" : inv.status]}`} data-testid="pay-invoice-status">{(isPaid ? "paid" : inv.status).toUpperCase()}</span>
        </div>

        <div className="border rounded-xl overflow-hidden mb-4">
          <table className="w-full text-sm">
            <thead className="bg-muted/50"><tr><th className="text-left p-2.5 font-medium">Description</th><th className="text-center p-2.5 font-medium">Qty</th><th className="text-right p-2.5 font-medium">Amount</th></tr></thead>
            <tbody>
              {(inv.items || []).map((it, i) => (
                <tr key={i} className="border-t"><td className="p-2.5">{it.description}</td><td className="p-2.5 text-center">{it.qty}</td><td className="p-2.5 text-right">{fmt((it.qty || 0) * (it.unit_price || 0), inv.currency)}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="text-right text-sm space-y-0.5 mb-6">
          <p className="text-muted-foreground">Subtotal {fmt(inv.subtotal, inv.currency)} · Tax ({inv.tax_rate || 0}%) {fmt(inv.tax, inv.currency)}</p>
          <p className="text-2xl font-bold" data-testid="pay-invoice-total">{fmt(inv.total, inv.currency)}</p>
          {inv.due_date && !isPaid && <p className="text-xs text-muted-foreground">Due {inv.due_date}</p>}
        </div>
        {inv.notes && <p className="text-xs text-muted-foreground bg-muted/40 rounded-lg p-3 mb-6">{inv.notes}</p>}

        {!isPaid && data.business?.payment_instructions && (
          <div className="text-xs bg-sky-500/5 border border-sky-500/20 rounded-lg p-3 mb-4 whitespace-pre-line" data-testid="pay-instructions">
            <p className="font-semibold mb-1">How to pay</p>
            {data.business.payment_instructions}
          </div>
        )}

        {!isPaid && (
          data.business?.payment_link ? (
            <Button className="w-full h-12 text-base bg-gradient-to-r from-sky-500 to-teal-500 hover:from-sky-600 hover:to-teal-600 text-white" onClick={() => window.open(data.business.payment_link, "_blank", "noopener")} data-testid="pay-external-btn">
              <CreditCard className="h-5 w-5 mr-2" />Pay {fmt(inv.total, inv.currency)} online
            </Button>
          ) : (
            <Button className="w-full h-12 text-base bg-gradient-to-r from-sky-500 to-teal-500 hover:from-sky-600 hover:to-teal-600 text-white" onClick={startPayment} disabled={paying} data-testid="pay-now-btn">
              {paying ? <Loader2 className="h-5 w-5 animate-spin" /> : <><CreditCard className="h-5 w-5 mr-2" />Pay {fmt(inv.total, inv.currency)} securely</>}
            </Button>
          )
        )}
        <p className="text-[10px] text-muted-foreground text-center mt-4">{data.business?.payment_link ? `Payment handled by ${data.business.business_name || "the vendor"}` : "Secure payment powered by Stripe"}</p>
      </div>
    </div>
  );
}

export function ClientPortalPage({ token }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${API}/public/portal/${token}`, { withCredentials: false })
      .then((r) => setData(r.data))
      .catch(() => setError("Portal not found or link expired."));
  }, [token]);

  if (error) return <div className="min-h-screen flex items-center justify-center bg-muted/30 p-6"><p className="text-sm text-muted-foreground" data-testid="portal-error">{error}</p></div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  const open = data.invoices.filter((i) => i.status !== "paid" && i.status !== "draft");
  const outstanding = open.reduce((s, i) => s + (i.total || 0), 0);

  return (
    <div className="min-h-screen bg-muted/30 py-10 px-4" data-testid="client-portal-page">
      <div className="max-w-2xl mx-auto">
        <div className="bg-card border rounded-2xl shadow-sm p-6 sm:p-8 mb-4">
          <BizHeader business={data.business} />
          <h1 className="text-xl font-bold mb-1">Your invoices</h1>
          <p className="text-sm text-muted-foreground" data-testid="portal-summary">{data.client_email} · {data.invoices.length} invoice{data.invoices.length === 1 ? "" : "s"}{outstanding > 0 ? ` · ${fmt(outstanding)} outstanding` : " · all settled 🎉"}</p>
        </div>
        <div className="space-y-3">
          {data.invoices.filter((i) => i.status !== "draft").map((inv) => (
            <div key={inv.number} className="bg-card border rounded-xl p-4 flex items-center justify-between gap-3" data-testid={`portal-invoice-${inv.number}`}>
              <div className="flex items-center gap-3 min-w-0">
                <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-semibold">{inv.number} <span className={`ml-1 text-[10px] font-semibold px-2 py-0.5 rounded-full ${STATUS_CLS[inv.status]}`}>{inv.status.toUpperCase()}</span></p>
                  <p className="text-xs text-muted-foreground">{inv.issue_date}{inv.due_date ? ` · due ${inv.due_date}` : ""}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <span className="text-sm font-bold">{fmt(inv.total)}</span>
                <a href={`/pay/${inv.public_token}`} className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline" data-testid={`portal-open-${inv.number}`}>
                  {inv.status === "paid" ? "View" : "Pay"}<ExternalLink className="h-3 w-3" />
                </a>
              </div>
            </div>
          ))}
          {data.invoices.filter((i) => i.status !== "draft").length === 0 && <p className="text-sm text-muted-foreground text-center py-8">No invoices yet.</p>}
        </div>
        <p className="text-[10px] text-muted-foreground text-center mt-6">Powered by Opero Smart</p>
      </div>
    </div>
  );
}
